import { puxarTudo } from './puxar'
import { empurrarOutbox } from './outbox'
import { definirInfoSync } from './estadoSync'
import { definirExecutorDeSync } from './pedirSync'
import { banco } from '../db/banco'
import { agoraISO } from '../db/tipos'

// Espera crescente depois de uma falha. O caso que isto resolve e o mais comum
// da rua: dentro da loja o sinal nao vai, e volta em alguns segundos. O celular
// NAO fica offline para o sistema nessa hora, entao o aviso de "a rede voltou"
// nunca dispara -- sem esta escada, o proximo envio so aconteceria no tick de 15
// minutos, e ate la o pedido existe num lugar so: o aparelho dela.
const ESPERAS_APOS_FALHA_MS = [5_000, 15_000, 45_000, 120_000, 300_000]
let tentativasSeguidas = 0
let timerDeRecuperacao: ReturnType<typeof setTimeout> | null = null

let emAndamento = false
// Se pedirem sync enquanto um ja roda, nao descartamos o pedido: marcamos para
// re-executar ao terminar. Sem isso, um "sincronizar agora" disparado enquanto um
// sync anterior esta preso (ex.: pull iniciado antes de cair a rede) seria perdido,
// e mutacoes offline nao subiriam mesmo apos reconectar.
let pedidoPendente = false
// ...e se o pedido que chegou no meio queria o ciclo COMPLETO, a proxima volta
// tem de baixar tambem -- senao um "sincronizar agora" dela viraria so um envio.
let pullPendente = false

// Travado nao conta: o app ja desistiu dele, e insistir viraria uma tentativa a
// cada cinco minutos para sempre. So conta o que ainda tem chance de subir.
async function restaAlgoParaSubir(): Promise<boolean> {
  const total = await banco.outbox.count()
  const travados = await banco.outbox.where('travado_em').above('').count()
  return total - travados > 0
}

function agendarNovaTentativa(): void {
  if (timerDeRecuperacao) return // ja ha uma a caminho
  const posicao = Math.min(tentativasSeguidas, ESPERAS_APOS_FALHA_MS.length - 1)
  tentativasSeguidas++
  timerDeRecuperacao = setTimeout(() => {
    timerDeRecuperacao = null
    void enviarPendencias()
  }, ESPERAS_APOS_FALHA_MS[posicao])
}

function pararDeTentar(): void {
  tentativasSeguidas = 0
  if (timerDeRecuperacao) {
    clearTimeout(timerDeRecuperacao)
    timerDeRecuperacao = null
  }
}

// O ciclo completo: sobe o que esta na fila e baixa o que mudou no servidor.
// E o que roda no periodico, ao voltar a rede, ao reabrir o app e no clique dela.
export async function sincronizar(): Promise<void> {
  await rodar(true)
}

// So SOBE. E o que serve depois de gravar e nas tentativas de recuperacao: nessas
// horas ninguem esta esperando dado novo do servidor, e baixar o catalogo inteiro
// a cada pedido salvo gastaria o pacote de dados dela no meio da rua.
export async function enviarPendencias(): Promise<void> {
  await rodar(false)
}

async function rodar(comPull: boolean): Promise<void> {
  if (!navigator.onLine) {
    definirInfoSync({ estado: 'offline' })
    return
  }
  if (emAndamento) {
    pedidoPendente = true
    if (comPull) pullPendente = true
    return
  }
  emAndamento = true
  definirInfoSync({ estado: 'sincronizando', erro: null })
  try {
    let puxarNestaVolta = comPull
    do {
      pedidoPendente = false
      pullPendente = false
      await empurrarOutbox()
      if (puxarNestaVolta) await puxarTudo()
      puxarNestaVolta = pullPendente
    } while (pedidoPendente && navigator.onLine)
    definirInfoSync({ estado: 'ocioso', ultimaSync: agoraISO(), erro: null })
  } catch (erro) {
    const msg = erro instanceof Error ? erro.message : String(erro)
    // Falha por falta de rede nao e "erro" de verdade: mostramos "Offline" (nao vermelho).
    if (!navigator.onLine || /failed to fetch|network|fetch failed|load failed|err_internet_disconnected/i.test(msg)) {
      definirInfoSync({ estado: 'offline' })
    } else {
      definirInfoSync({ estado: 'erro', erro: msg })
    }
  } finally {
    emAndamento = false
  }

  // Sobrou coisa na fila? Entao algo impediu o envio, e a escada assume. So
  // quando a fila esvazia a escada volta ao primeiro degrau.
  //
  // A tentacao aqui era zerar a escada a cada envio bem-sucedido -- "deu certo,
  // logo o caminho voltou". Seria um erro: basta UM registro que falha sempre
  // (erro sem codigo do banco nunca trava, fica na fila indefinidamente) para
  // que cada pedido novo que sobe zere a contagem, e o app passe a bater no
  // servidor de 5 em 5 segundos o dia inteiro. A escada existe justamente para
  // isso nao acontecer.
  if (await restaAlgoParaSubir()) agendarNovaTentativa()
  else pararDeTentar()
}

const INTERVALO_MS = 15 * 60 * 1000

export function iniciarSyncAutomatico(): () => void {
  definirExecutorDeSync(() => void enviarPendencias())
  const aoVoltarOnline = () => void sincronizar()
  const aoGanharFoco = () => {
    if (document.visibilityState === 'visible') void sincronizar()
  }
  window.addEventListener('online', aoVoltarOnline)
  document.addEventListener('visibilitychange', aoGanharFoco)
  const intervalo = setInterval(() => void sincronizar(), INTERVALO_MS)
  void sincronizar() // primeira carga
  return () => {
    pararDeTentar()
    definirExecutorDeSync(null)
    window.removeEventListener('online', aoVoltarOnline)
    document.removeEventListener('visibilitychange', aoGanharFoco)
    clearInterval(intervalo)
  }
}
